import { __ } from "@wordpress/i18n";
import apiFetch from "@wordpress/api-fetch";
import { useState, useEffect } from "react";
import Header from "../../layouts/Header/Header";
import Footer from "../../layouts/Footer/Footer";
import { useFormSave } from "../../hooks/useFormSave";
import setupSteps from "./SetupSteps";
import SetupComplete from "./SetupComplete";
import useAdminMenuHighlight from '../../hooks/useAdminMenuHighlight';
import "bootstrap/dist/css/bootstrap.min.css";
import "./SetupWizard.scss";


const SetupWizard = () => {
	useAdminMenuHighlight(); 
	const [currentStep, setCurrentStep] = useState(0);
	const [isComplete, setIsComplete] = useState(false);
	const [isFinishing, setIsFinishing] = useState(false);

	const step = setupSteps[currentStep];

	// Dynamically get sectionPath
	const sectionPath = step?.componentRef?.sectionPath || null;

	const { handleSave } = useFormSave(sectionPath);

	const handleNext = async () => {
		const success = await handleSave();
		if (success) {
			setCurrentStep((prev) => prev + 1);
		}
	};

	const handlePrevious = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};

	const handleSkip = () => {
		window.location.href = ultimate_security_ajax_obj.admin_url + 'admin.php?page=ultimate-security';
	};

	const handleDone = async () => {
		setIsFinishing(true);
		
		try {
			// First save the current step if needed
			const saveSuccess = await handleSave();
			if (!saveSuccess) {
				setIsFinishing(false);
				return;
			}

			// Then mark setup as complete via API
			await apiFetch({
				url: "/wp-json/ultimate-security/v1/setup-complete",
				method: "POST",
			});

			// Mark as complete to show SetupComplete component
			setIsComplete(true);
			
		} catch (error) {
			console.error('Setup completion failed:', error);
			// Optionally show error message to user
		} finally {
			setIsFinishing(false);
		}
	};

	const SetupWizardHeader = () => {
		return (
			<header className="setup-wizard-header border-bottom">
				<div className="container-fluid">
					<div className="d-flex justify-content-center align-items-center">
						<h4 className="setup-wizard-title section-title pl-heading fw-700 text-text">
							{setupSteps[currentStep].title}
						</h4>
					</div>
					<div className="d-flex justify-content-center align-items-center">
						<p className="setup-wizard-description section-intro pl-body text-text">
							{setupSteps[currentStep].description}
						</p>
					</div>
				</div>
			</header>
		);
	};

	const SetupWizardFooter = () => {
		return (
			<footer >
				<div className="setup-wizard-footer">
					<div className="container-fluid">
						<div className="d-flex justify-content-between align-items-center py-3">
							<div className="footer-left"></div>
							<div className="footer-center">
								<div className="step-indicators d-flex align-items-center gap-2">
									{setupSteps.map((_, index) => (
										<div
											key={index}
											className={`step-dot ${
												index === currentStep ? 'active' : ''
											} ${index < currentStep ? 'completed' : ''}`}
											style={{
												width: '8px',
												height: '8px',
												borderRadius: '50%',
												backgroundColor: index === currentStep 
													? '#873eff' 
													: index < currentStep 
														? '#873eff' 
														: '#ddd',
												cursor: 'pointer'
											}}
											onClick={() => setCurrentStep(index)}
										/>
									))}
								</div>
							</div>
							<div className="footer-right d-flex gap-2">
								{currentStep > 0 && (
									<button
										className="button button-link"
										onClick={handlePrevious}
										disabled={isFinishing}
									>
										{__("Previous", "ultimate-security")}
									</button>
								)}
								{currentStep < setupSteps.length - 1 && (
									<button
										className="button button-secondary"
										onClick={handleNext}
										disabled={isFinishing}
									>
										{__("Next", "ultimate-security")}
									</button>
								)}
								{currentStep === setupSteps.length - 1 && (
									<button
										className="button button-secondary"
										onClick={handleDone}
										disabled={isFinishing}
									>
										{isFinishing 
											? __("Finishing...", "ultimate-security")
											: __("Finish Setup", "ultimate-security")
										}
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
				<div className="d-flex justify-content-center pb-lg mb-lg pt-4">
					<button className="button button-link" onClick={handleSkip}>{__("Skip Setup Wizard", "ultimate-security")}</button>
				</div>
			</footer>
		);
	};

	const SetupWizardContent = () => {
		return (
			<div className="ultimate-security-setup-wizard-wrapper">
				<SetupWizardHeader />
				<div className="main-content">
					{setupSteps[currentStep].component}
				</div>
				<SetupWizardFooter />
			</div>
		);
	};

	return (
		<>
			<style>
				{`
				.form-footer {
					display: none;
				}
				.ultimate-security-topbar .topbar-left li {
					display: none;
				}
				a.search-settings {
					display: none;
				}
				.col-auto.header-logos.notification {
					display: none;
				}
			`}
			</style>
			<div className="ultimate-security-settings-container">
				<Header />

				{isComplete ? (
					<SetupComplete />
				) : (
					<SetupWizardContent />
				)}
				
				<Footer />
			</div>
		</>
	);
};

export default SetupWizard;
